/**
 * GlassmorphismButton - TV-optimized glassmorphism buttons
 * Matches Flutter's _TvActionButton styling from tv_detail_hero.dart
 * 
 * Variants:
 * - primary: Red gradient for main actions (Play)
 * - secondary: Gray gradient for secondary actions (Preview)
 * - icon: Circular button for like/dislike/bookmark
 * 
 * LEGACY CSS: No backdrop-blur, uses gradient backgrounds
 */
import React from 'react';
import { useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { useInputMode } from '../../utils/hooks/useInputMode';

/**
 * GlassmorphismButton - Primary/Secondary action button
 */
export const GlassmorphismButton = ({
    label,
    icon,
    variant = 'primary',
    loading = false,
    autofocus = false,
    focusKey,
    onEnterPress,
    onFocus,
}) => {
    const { ref, focused, focusSelf } = useFocusable({
        focusKey,
        onEnterPress,
        onFocus,
        focusable: !loading,
    });

    const { setMouseFocus } = useInputMode();

    React.useEffect(() => {
        if (autofocus) {
            focusSelf();
        }
    }, [autofocus, focusSelf]);

    const handleMouseEnter = () => {
        setMouseFocus(true);
        focusSelf();
    };

    const handleClick = () => {
        if (!loading && onEnterPress) {
            onEnterPress();
        }
    };

    return (
        <button
            ref={ref}
            onMouseEnter={handleMouseEnter}
            onClick={handleClick}
            disabled={loading}
            type="button"
            className={"legacy-glass-button legacy-glass-button-" + variant + (focused ? " is-focused" : "")}
        >
            {loading ? (
                <span className="legacy-glass-spinner" />
            ) : (
                <>
                    {icon && <span className="legacy-glass-button-icon">{icon}</span>}
                    <span className="legacy-glass-button-label">{label}</span>
                </>
            )}
        </button>
    );
};

/**
 * GlassmorphismIconButton - Circular icon button for actions
 */
export const GlassmorphismIconButton = ({
    icon,
    focusKey,
    onEnterPress,
    onFocus,
}) => {
    const { ref, focused, focusSelf } = useFocusable({
        focusKey,
        onEnterPress,
        onFocus,
    });

    const { setMouseFocus } = useInputMode();

    const handleMouseEnter = () => {
        setMouseFocus(true);
        focusSelf();
    };

    const handleClick = () => {
        if (onEnterPress) {
            onEnterPress();
        }
    };

    return (
        <button
            ref={ref}
            onMouseEnter={handleMouseEnter}
            onClick={handleClick}
            type="button"
            className={"legacy-glass-icon-button" + (focused ? " is-focused" : "")}
        >
            {icon}
        </button>
    );
};

export default GlassmorphismButton;
